import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../../api/admin';

export const adminKeys = {
    all: ['admin'] as const,
    scrapers: () => [...adminKeys.all, 'scrapers'] as const,
    users: () => [...adminKeys.all, 'users'] as const,
    userSubscriptions: (userId: string) => [...adminKeys.users(), userId, 'subscriptions'] as const,
    systemHealth: () => [...adminKeys.all, 'health'] as const,
    schedules: () => [...adminKeys.all, 'schedules'] as const,
    scheduleProfiles: () => [...adminKeys.all, 'scheduleProfiles'] as const,
    scraperSchedule: (searchKey: string) => [...adminKeys.all, 'scraperSchedule', searchKey] as const,
    scraperBackoff: (searchKey: string) => [...adminKeys.all, 'scraperBackoff', searchKey] as const,
};

export const useScrapers = () => {
    return useQuery({
        queryKey: adminKeys.scrapers(),
        queryFn: adminApi.getScrapers,
        staleTime: 30_000,
        refetchInterval: 30000,
    });
};

export const useUsers = () => {
    return useQuery({
        queryKey: adminKeys.users(),
        queryFn: adminApi.getUsers,
        staleTime: 30_000,
    });
};

export const useSystemHealth = (refetchInterval?: number) => {
    return useQuery({
        queryKey: adminKeys.systemHealth(),
        queryFn: adminApi.getSystemHealth,
        staleTime: 30_000,
        refetchInterval: refetchInterval ?? 60000,
    });
};

export const useUserSubscriptions = (userId: string, enabled: boolean) => {
    return useQuery({
        queryKey: adminKeys.userSubscriptions(userId),
        queryFn: () => adminApi.getUserSubscriptions(userId),
        staleTime: 60_000,
        enabled,
    });
};

// Mutations

export const useToggleScraper = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ searchKey, activate }: { searchKey: string; activate: boolean }) =>
            adminApi.toggleScraper(searchKey, activate),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scrapers() });
        },
    });
};

export const useUpdateScraperNote = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ searchKey, note }: { searchKey: string; note: string }) =>
            adminApi.updateScraperNote(searchKey, note),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scrapers() });
        },
    });
};

export const useAcknowledgeError = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: adminApi.acknowledgeError,
        onSuccess: (_data, searchKey) => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scrapers() });
            // Acknowledging resets errorCounter/lastErrorTimestamp server-side,
            // so the live backoff view (shown in ScraperScheduleBadge) must be
            // refetched too, otherwise it would keep showing the stale delay.
            queryClient.invalidateQueries({ queryKey: adminKeys.scraperBackoff(searchKey) });
        },
    });
};

export const useScheduleProfiles = () => {
    return useQuery({
        queryKey: adminKeys.scheduleProfiles(),
        queryFn: adminApi.getScheduleProfiles,
        staleTime: 30_000,
    });
};

export const useCreateScheduleProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: Omit<adminApi.ScheduleProfile, 'id'>) => adminApi.createScheduleProfile(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scheduleProfiles() });
        },
    });
};

export const useUpdateScheduleProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: Omit<adminApi.ScheduleProfile, 'id'> }) =>
            adminApi.updateScheduleProfile(id, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scheduleProfiles() });
        },
    });
};

export const useDeleteScheduleProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => adminApi.deleteScheduleProfile(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scheduleProfiles() });
        },
    });
};

export const useScraperSchedule = (searchKey: string, enabled: boolean) => {
    return useQuery({
        queryKey: adminKeys.scraperSchedule(searchKey),
        queryFn: () => adminApi.getScraperSchedule(searchKey),
        enabled,
    });
};

export const useSetScraperSchedule = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ searchKey, profileId, regularIntervalMinutes }: { searchKey: string; profileId: string; regularIntervalMinutes: number }) =>
            adminApi.setScraperSchedule(searchKey, { profileId, regularIntervalMinutes }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scraperSchedule(vars.searchKey) });
            queryClient.invalidateQueries({ queryKey: adminKeys.scrapers() });
        },
    });
};

export const useScraperBackoffState = (searchKey: string, enabled: boolean) => {
    return useQuery({
        queryKey: adminKeys.scraperBackoff(searchKey),
        queryFn: () => adminApi.getScraperBackoffState(searchKey),
        enabled,
    });
};

export const useSetScraperBackoffOverride = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ searchKey, override }: { searchKey: string; override: adminApi.BackoffOverride | null }) =>
            adminApi.setScraperBackoffOverride(searchKey, override),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scraperBackoff(vars.searchKey) });
        },
    });
};

// Helper for optimistic user list updates
const optimisticUserUpdate = (queryClient: ReturnType<typeof useQueryClient>) => ({
    onMutate: async (vars: { userId: string } & Record<string, any>) => {
        await queryClient.cancelQueries({ queryKey: adminKeys.users() });
        const previous = queryClient.getQueryData<adminApi.UserData[]>(adminKeys.users());
        return { previous };
    },
    onError: (_err: any, _vars: any, ctx: { previous?: adminApi.UserData[] } | undefined) => {
        if (ctx?.previous) queryClient.setQueryData(adminKeys.users(), ctx.previous);
    },
    onSettled: () => {
        queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
});

export const useUpdateUserTier = () => {
    const queryClient = useQueryClient();
    const opts = optimisticUserUpdate(queryClient);
    return useMutation({
        mutationFn: ({ userId, tier }: { userId: string; tier: string }) =>
            adminApi.updateUserTier(userId, tier),
        onMutate: async (vars) => {
            const ctx = await opts.onMutate(vars);
            queryClient.setQueryData<adminApi.UserData[]>(adminKeys.users(), (old) =>
                old?.map(u => u.userId === vars.userId ? { ...u, tier: vars.tier } : u)
            );
            return ctx;
        },
        onError: opts.onError,
        onSettled: opts.onSettled,
    });
};

export const useUpdateUserTelegram = () => {
    const queryClient = useQueryClient();
    const opts = optimisticUserUpdate(queryClient);
    return useMutation({
        mutationFn: ({ userId, telegramId }: { userId: string; telegramId: string | number }) =>
            adminApi.updateUserTelegram(userId, telegramId),
        onMutate: async (vars) => {
            const ctx = await opts.onMutate(vars);
            queryClient.setQueryData<adminApi.UserData[]>(adminKeys.users(), (old) =>
                old?.map(u => u.userId === vars.userId ? { ...u, telegramId: Number(vars.telegramId) } : u)
            );
            return ctx;
        },
        onError: opts.onError,
        onSettled: opts.onSettled,
    });
};

export const useUpdateUserExpiry = () => {
    const queryClient = useQueryClient();
    const opts = optimisticUserUpdate(queryClient);
    return useMutation({
        mutationFn: ({ userId, expiry }: { userId: string; expiry: number }) =>
            adminApi.updateUserExpiry(userId, expiry),
        onMutate: async (vars) => {
            const ctx = await opts.onMutate(vars);
            queryClient.setQueryData<adminApi.UserData[]>(adminKeys.users(), (old) =>
                old?.map(u => u.userId === vars.userId ? { ...u, premiumExpiresAt: vars.expiry } : u)
            );
            return ctx;
        },
        onError: opts.onError,
        onSettled: opts.onSettled,
    });
};

export const useUpdateUserEmail = () => {
    const queryClient = useQueryClient();
    const opts = optimisticUserUpdate(queryClient);
    return useMutation({
        mutationFn: ({ userId, email }: { userId: string; email: string | null }) =>
            adminApi.updateUserEmail(userId, email),
        onMutate: async (vars) => {
            const ctx = await opts.onMutate(vars);
            queryClient.setQueryData<adminApi.UserData[]>(adminKeys.users(), (old) =>
                old?.map(u => u.userId === vars.userId
                    ? { ...u, email: vars.email ?? undefined, ...(vars.email === null ? { emailVerified: false, verificationToken: null, verificationTokenExpires: null } : {}) }
                    : u)
            );
            return ctx;
        },
        onError: opts.onError,
        onSettled: opts.onSettled,
    });
};

export const useResetUserPassword = () => {
    return useMutation({
        mutationFn: ({ userId }: { userId: string }) => adminApi.resetUserPassword(userId),
        // No invalidation needed usually, or invalidate users if hasPassword flag changes?
        // Assuming hasPassword might update (not in interface yet but possible), safe to invalidate
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: adminKeys.users() }); 
        }
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId }: { userId: string }) => adminApi.deleteUserAccount(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.users() });
        },
    });
};

export const useSendPushTest = () => {
    return useMutation({
        mutationFn: ({ userId }: { userId: string }) => adminApi.sendPushTest(userId),
    });
};

export const useSendTestEmail = () => {
    return useMutation({
        mutationFn: ({ userId }: { userId: string }) => adminApi.sendTestEmail(userId),
    });
};

export const useSendTestTelegram = () => {
    return useMutation({
        mutationFn: ({ userId }: { userId: string }) => adminApi.sendTestTelegram(userId),
    });
};

export const useRebuildSystem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ type, force }: { type: 'metadata'; force?: boolean }) => adminApi.rebuildSystem(type, !!force),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.all });
        },
    });
};

export const useUpdateAdminSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ subscriptionId, data }: { subscriptionId: string; data: any; userId: string }) =>
            adminApi.updateSubscription(subscriptionId, data),
        onSuccess: (updatedSub, { subscriptionId, userId }) => {
            // 1. Update the detail in useSubscriptions cache if it exists
            // (Shared keys prefix)
            queryClient.setQueryData(['subscriptions', 'detail', subscriptionId], updatedSub);

            // 2. Invalidate the user-specific admin list
            queryClient.invalidateQueries({ queryKey: adminKeys.userSubscriptions(userId) });

            // 3. Optional: invalidate global lists to be safe
            queryClient.invalidateQueries({ queryKey: ['subscriptions', 'global-list'] });
        },
    });
};

export const useDeleteAdminSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ subscriptionId }: { subscriptionId: string; userId: string }) =>
            adminApi.deleteSubscription(subscriptionId),
        onSuccess: (_, { subscriptionId, userId }) => {
            queryClient.removeQueries({ queryKey: ['subscriptions', 'detail', subscriptionId] });
            queryClient.invalidateQueries({ queryKey: adminKeys.userSubscriptions(userId) });
            queryClient.invalidateQueries({ queryKey: ['subscriptions', 'global-list'] });
        },
    });
};

export const useSendGlobalNotification = () => {
    return useMutation({
        mutationFn: (data: {
            message: string;
            channels: ('telegram' | 'push' | 'email')[];
            type: 'maintenance' | 'force-reload';
            title?: string;
        }) => adminApi.sendGlobalNotification(data),
    });
};

export const useSetMaintenanceMode = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { active: boolean; severity?: string; text?: string }) =>
            adminApi.setMaintenanceMode(data),
        onSuccess: () => {
            // Invalidate both admin health and public health so all consumers refresh
            queryClient.invalidateQueries({ queryKey: adminKeys.systemHealth() });
            queryClient.invalidateQueries({ queryKey: ['health'] });
        },
    });
};

export const useStaleScrapers = (enabled: boolean) => {
    return useQuery({
        queryKey: [...adminKeys.scrapers(), 'stale'] as const,
        queryFn: adminApi.getStaleScrapers,
        enabled,
    });
};

export const useDeleteStaleScrapers = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: adminApi.deleteStaleScrapers,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.scrapers() });
        },
    });
};

export const useScraperSchedules = () => {
    return useQuery({
        queryKey: adminKeys.schedules(),
        queryFn: adminApi.getScraperSchedules,
        staleTime: 60_000,
    });
};

export const useToggleScraperSchedules = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (enabled: boolean) => adminApi.toggleScraperSchedules(enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adminKeys.schedules() });
        },
    });
};

export const useTriggerScraperRun = () => {
    return useMutation({
        mutationFn: ({ scraperFilter, testMode }: { scraperFilter?: string[]; testMode?: boolean } = {}) =>
            adminApi.triggerScraperRun(scraperFilter, testMode),
    });
};
